/**
 * Chuông thông báo ở thanh đầu màn.
 *
 * Lý do tồn tại: tin quan trọng nhất của hệ — "tiền về đơn của bạn" — trước
 * đây chỉ đi qua Telegram. Nhân sự chưa liên kết Telegram thì không bao giờ
 * biết, và không có chỗ nào đọc lại tin đã trôi qua.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Bell, Volume2, VolumeX } from "lucide-react";

import { Button } from "@/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/ui/dropdown-menu";
import { Skeleton } from "@/ui/skeleton";
import { useMarkRead, useNotifications, useUnreadCount, type StaffNotification } from "./api";
import { NotificationBody } from "./NotificationBody";
import { NotificationExtraLink } from "./NotificationExtraLink";
import { NotificationGroupIcon } from "./NotificationGroupIcon";
import { dayLabel, displayTitle } from "./notification-body";
import { useOpenNotification } from "./open-notification";
import { isSoundMuted, playDing, setSoundMuted, useDingOnNewNotification } from "./sound";
import { ago } from "./time-ago";

/**
 * `side` / `align`: hộp mở về phía nào. Mặc định mở xuống, canh mép phải (chuông
 * ở góc phải thanh đầu). Dải biểu tượng của màn Chat khách đặt chuông ở mép
 * TRÁI dưới đáy màn nên mở sang phải.
 */
export function NotificationBell({
  side = "bottom",
  align = "end",
}: { side?: "bottom" | "right"; align?: "start" | "end" } = {}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unread = useUnreadCount();
  const list = useNotifications(open);
  const markRead = useMarkRead();
  const openNotification = useOpenNotification();
  const [muted, setMuted] = useState(isSoundMuted);

  const total = unread.data?.total ?? 0;
  useDingOnNewNotification(unread.data?.total, unread.data?.orderIds);

  const toggleSound = () => {
    const next = !muted;
    setSoundMuted(next);
    setMuted(next);
    // Bật lại thì kêu thử một tiếng: người dùng biết loa máy mình có nghe được.
    if (!next) playDing();
  };
  const shownUnread = (list.data ?? []).filter((n) => !n.readAt).length;
  const hiddenUnread = list.data ? Math.max(0, total - shownUnread) : 0;

  const openItem = (n: StaffNotification) => {
    setOpen(false);
    return openNotification(n);
  };

  // Trang "Thông báo của tôi" (20/10/2026): chuông chỉ có 50 tin mới nhất,
  // muốn lật lại tin cũ, lọc, tìm thì sang trang.
  const goToPage = (unreadOnly: boolean) => {
    setOpen(false);
    void navigate(
      unreadOnly
        ? ({ to: "/notifications", search: { status: "unread" } } as never)
        : ({ to: "/notifications" } as never),
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative min-h-[var(--touch-min)] min-w-[var(--touch-min)]"
          aria-label={total > 0 ? `Thông báo, ${total} chưa đọc` : "Thông báo"}
        >
          <Bell size={20} />
          {total > 0 && (
            <span
              data-testid="notif-dot"
              className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-red-600 px-1 text-[10px] font-bold leading-[18px] text-white"
            >
              {total > 9 ? "9+" : total}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent side={side} align={align} className="w-[min(92vw,380px)] p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-semibold">Thông báo</span>
          <div className="flex items-center gap-2">
            {total > 0 && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:underline"
                onClick={() => void markRead.mutateAsync({ all: true }).catch(() => {})}
              >
                Đánh dấu đã đọc tất cả
              </button>
            )}
            <button
              type="button"
              onClick={toggleSound}
              aria-pressed={!muted}
              aria-label={muted ? "Bật tiếng báo tin mới" : "Tắt tiếng báo tin mới"}
              title={muted ? "Đang tắt tiếng — bấm để bật" : "Đang bật tiếng — bấm để tắt"}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : list.isError && !list.data ? (
            // Tải hỏng KHÔNG phải hộp thư trống: nói "chưa có thông báo" lúc này
            // là nói dối, người dùng bỏ đi tưởng hộp trống trong khi đang lỡ tin
            // (kiểm toán 05/09/2026, OPS-UI-01).
            <div className="px-3 py-6 text-center text-sm">
              <p className="text-destructive">Không tải được hộp thư.</p>
              <button
                type="button"
                onClick={() => void list.refetch()}
                className="mt-2 text-sm underline"
              >
                Thử lại
              </button>
            </div>
          ) : (list.data ?? []).length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              Chưa có thông báo nào.
            </p>
          ) : (
            <>
              {list.isError ? (
                // Còn bản cũ thì giữ nguyên danh sách, chỉ nhắc là chưa làm mới được.
                <div className="flex items-center justify-between border-b bg-amber-50/60 px-3 py-1.5 text-xs text-amber-800">
                  <span>Không làm mới được, đang hiện bản cũ.</span>
                  <button type="button" onClick={() => void list.refetch()} className="underline">
                    Thử lại
                  </button>
                </div>
              ) : null}
              <ul>
                {(list.data ?? []).map((n, i, all) => {
                  // Tin xếp mới nhất trước nên cùng ngày nằm liền nhau: đầu mỗi ngày một dòng ngày.
                  const day = dayLabel(n.createdAt);
                  const newDay = i === 0 || dayLabel(all[i - 1]?.createdAt ?? "") !== day;
                  return (
                    <li key={n.id} className="border-b last:border-0">
                      {newDay ? (
                        <div className="bg-muted/50 px-3 py-1 text-[11px] font-medium text-muted-foreground">
                          {day}
                        </div>
                      ) : null}
                      {/* Cả thẻ bấm được; nút tiêu đề cho bàn phím, nút "Xem cả" tự chặn nổi lên. */}
                      <div
                        onClick={() => void openItem(n)}
                        className={`flex cursor-pointer gap-2.5 px-3 py-2.5 hover:bg-muted/40 ${n.readAt ? "" : "bg-emerald-50/60"}`}
                      >
                        <NotificationGroupIcon group={n.group} className="mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void openItem(n);
                              }}
                              className={
                                "min-w-0 break-words text-left text-sm leading-snug " +
                                (n.readAt
                                  ? "font-normal text-muted-foreground"
                                  : "font-semibold text-foreground")
                              }
                            >
                              {displayTitle(n.title)}
                            </button>
                            <time
                              dateTime={n.createdAt}
                              className="mt-0.5 shrink-0 whitespace-nowrap text-[11px] text-muted-foreground"
                            >
                              {ago(n.createdAt)}
                            </time>
                          </div>
                          {n.body ? <NotificationBody body={n.body} /> : null}
                          <span onClick={(e) => e.stopPropagation()}>
                            <NotificationExtraLink n={n} className="mt-1" />
                          </span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {hiddenUnread > 0 ? (
                // Số đỏ đếm MỌI tin chưa đọc; danh sách chỉ có các tin mới nhất.
                <button
                  type="button"
                  onClick={() => goToPage(true)}
                  className="block w-full border-t px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted/40 hover:underline"
                >
                  Còn {hiddenUnread} tin chưa đọc cũ hơn không hiện ở đây — xem ở trang Thông báo của tôi
                </button>
              ) : null}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => goToPage(false)}
          className="block w-full border-t px-3 py-2 text-center text-sm font-medium text-primary hover:bg-muted/40"
        >
          Xem tất cả
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
